import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Card, Row, Col, Alert, Badge, ListGroup } from 'react-bootstrap'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { useSelector } from 'react-redux'

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RjLTXPf7U0kOHcwMl8aHLyumyrTGjRKw6OKCT59JkYyLtrLECMiEq0R9icSCQJ5lQP390Yyk8VUfNiSRSivnXV5004V5IiAjj')

interface Plan {
  id: string
  planName: string
  planDescription: string
  monthlyPrice: string
  yearlyPrice: string
  monthlyPriceRaw: number
  yearlyPriceRaw: number
  yearlySavings?: string | null
  planFeatures: string[]
  taglines: string[]
  stripeProductId: string
  stripePrices: {
    monthly: string | null
    yearly: string | null
  }
  planType: 'standard' | 'custom'
}

interface PaywallModalProps {
  show: boolean
  onHide: () => void
  paywallData: {
    experiences: any[]
    trialDaysRemaining: number
    sceneCount: number
    isOnActivate: boolean
  } | null
  onPaymentComplete: (paymentData: { success: boolean }) => void
}

const PlanCard: React.FC<{
  plan: Plan
  isSelected: boolean
  billingCycle: 'monthly' | 'yearly'
  onSelect: (planId: string, priceId: string) => void
}> = ({ plan, isSelected, billingCycle, onSelect }) => {
  const currentPrice = billingCycle === 'monthly' ? plan.monthlyPriceRaw : plan.yearlyPriceRaw
  const currentPriceId = billingCycle === 'monthly' ? plan.stripePrices.monthly : plan.stripePrices.yearly
  const displayPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
  const priceLabel = billingCycle === 'monthly' ? '/month' : '/year'
  
  // Don't render if the selected billing cycle is not available
  if ((billingCycle === 'monthly' && !plan.stripePrices.monthly) || 
      (billingCycle === 'yearly' && !plan.stripePrices.yearly)) {
    return null
  }

  return (
    <Card 
      className={`h-100 cursor-pointer mb-3 ${isSelected ? 'border-primary shadow' : 'border-light'}`}
      onClick={() => currentPriceId && onSelect(plan.id, currentPriceId)}
      style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
    >
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5 className="mb-1">{plan.planName}</h5>
            {plan.planType === 'custom' && (
              <Badge bg="info" className="mb-2">Exclusive Plan</Badge>
            )}
          </div>
          <Form.Check
            type="radio"
            name="selectedPlan"
            checked={isSelected}
            onChange={() => currentPriceId && onSelect(plan.id, currentPriceId)}
          />
        </div>

        <div className="text-center mb-3">
          <h3 className="text-primary mb-0">{displayPrice}</h3>
          <small className="text-muted">{priceLabel}</small>
          {billingCycle === 'yearly' && plan.yearlySavings && (
            <div className="text-success mt-1">
              <small><strong>Save {plan.yearlySavings}!</strong></small>
            </div>
          )}
        </div>

        <p className="text-muted small mb-3">{plan.planDescription}</p>

        {/* Features */}
        {plan.taglines && plan.taglines.length > 0 && (
          <div className="mt-auto">
            <h6 className="mb-2">Features:</h6>
            <ListGroup variant="flush" className="small">
              {plan.taglines.slice(0, 4).map((tagline, index) => (
                <ListGroup.Item key={index} className="px-0 py-1 border-0 bg-transparent">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  {tagline.trim()}
                </ListGroup.Item>
              ))}
              {plan.taglines.length > 4 && (
                <ListGroup.Item className="px-0 py-1 border-0 bg-transparent small text-muted">
                  +{plan.taglines.length - 4} more features
                </ListGroup.Item>
              )}
            </ListGroup>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

const PaymentForm: React.FC<{
  paywallData: any
  onPaymentComplete: (paymentData: { success: boolean }) => void
  onCancel: () => void
}> = ({ paywallData, onPaymentComplete, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const user = useSelector((state: any) => state.Auth?.authProfile?.userData)
  
  const [standardPlans, setStandardPlans] = useState<Plan[]>([])
  const [affiliatePlans, setAffiliatePlans] = useState<Plan[]>([])
  const [affiliateData, setAffiliateData] = useState<any>(null)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [selectedPriceId, setSelectedPriceId] = useState<string>('')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLoadingPlans, setIsLoadingPlans] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const functions = getFunctions()
  const getSubscriptionPlansForUser = httpsCallable(functions, 'getSubscriptionPlansForUser')
  const createPaymentSetup = httpsCallable(functions, 'createPaymentSetup')
  const createSceneSubscription = httpsCallable(functions, 'createSceneSubscription')

  // Load plans when component mounts and user data is available
  useEffect(() => {
    if (user?.protected?.email) {
      loadPlans()
    }
  }, [user])

  const loadPlans = async () => {
    try {
      setIsLoadingPlans(true)
      
      // Check if user data is available
      if (!user?.protected?.email) {
        console.warn('User data not available, cannot load plans')
        setError('User authentication required. Please refresh the page.')
        return
      }
      
      // Get affiliate ID from user if it exists
      const affiliateId = user?.protected?.affiliateId || null
      
      const result = await getSubscriptionPlansForUser({ 
        userEmail: user.protected.email,
        affiliateId 
      })
      
      const data = result.data as any
      
      if (data.result === 'ok') {
        setStandardPlans(data.standardPlans || [])
        setAffiliatePlans(data.affiliatePlans || [])
        setAffiliateData(data.affiliateData)
        
        // Auto-select first available plan
        const allPlans = [...(data.affiliatePlans || []), ...(data.standardPlans || [])]
        if (allPlans.length > 0) {
          const firstPlan = allPlans[0]
          const firstPriceId = billingCycle === 'monthly' ? firstPlan.stripePrices.monthly : firstPlan.stripePrices.yearly
          if (firstPriceId) {
            setSelectedPlanId(firstPlan.id)
            setSelectedPriceId(firstPriceId)
          }
        }
      }
    } catch (error) {
      console.error('Error loading plans:', error)
      setError('Failed to load subscription plans')
    } finally {
      setIsLoadingPlans(false)
    }
  }

  // Update selected price when billing cycle changes
  useEffect(() => {
    if (selectedPlanId) {
      const allPlans = [...affiliatePlans, ...standardPlans]
      const selectedPlan = allPlans.find(plan => plan.id === selectedPlanId)
      if (selectedPlan) {
        const newPriceId = billingCycle === 'monthly' ? selectedPlan.stripePrices.monthly : selectedPlan.stripePrices.yearly
        if (newPriceId) {
          setSelectedPriceId(newPriceId)
        }
      }
    }
  }, [billingCycle, selectedPlanId, affiliatePlans, standardPlans])

  const handlePlanSelect = (planId: string, priceId: string) => {
    setSelectedPlanId(planId)
    setSelectedPriceId(priceId)
    setError(null)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !selectedPlanId || !selectedPriceId) {
      setError('Please select a plan and ensure payment form is ready')
      return
    }

    setIsProcessing(true)
    setError(null)

    const cardElement = elements.getElement(CardElement)

    if (!cardElement) {
      setError('Card element not found')
      setIsProcessing(false)
      return
    }

    try {
      // Create payment setup intent
      const setupResult = await createPaymentSetup({ 
        planType: billingCycle,
        planId: selectedPlanId,
        priceId: selectedPriceId
      })
      const setupData = setupResult.data as any
      
      if (!setupData.success) {
        throw new Error(setupData.error || 'Failed to create payment setup')
      }

      // Confirm the setup intent with the card
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupData.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      )

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment setup failed')
      }

      console.log('priceid', selectedPriceId)
      if (setupIntent?.payment_method) {
        // Create subscription with affiliate tracking
        let sceneId = null
        if (paywallData?.isOnActivate) {
          sceneId = paywallData?.experiences?.[0]?.id || null
        }

        console.log('payload for createSceneSubscription', {
          planId: selectedPlanId,
          productId: selectedPlanId,
          priceId: selectedPriceId,
          paymentMethodId: setupIntent.payment_method as string,
          sceneId: sceneId
        })
        const subscriptionResult = await createSceneSubscription({
          planId: selectedPlanId,
          productId: selectedPlanId,
          priceId: selectedPriceId,
          paymentMethodId: setupIntent.payment_method as string,
          sceneId: sceneId
        })
        
        const subscriptionData = subscriptionResult.data as any
        if (!subscriptionData.success) {
          throw new Error('Failed to create subscriptions')
        }else{
            onPaymentComplete({
                success: true
              })
        }
      }

    } catch (error: any) {
      setError(error.message || 'Payment setup failed')
      
    } finally {
      setIsProcessing(false)
    }
  }

  const allPlans = [...affiliatePlans, ...standardPlans]
  const selectedPlan = allPlans.find(plan => plan.id === selectedPlanId)
  const hasBothBillingOptions = selectedPlan && selectedPlan.stripePrices.monthly && selectedPlan.stripePrices.yearly

  if (isLoadingPlans || !user) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">
          {!user ? 'Loading user data...' : 'Loading subscription plans...'}
        </p>
      </div>
    )
  }

  return (
    <Form onSubmit={handleSubmit}>
      {!paywallData?.isOnActivate && <div className="mb-4">
        <h5 className="mb-3">Complete your trial setup</h5>
        <p className="text-muted">
          You have {paywallData?.sceneCount} experiences that need a plan added to them: "{paywallData?.experiences?.map((experience: any) => experience.name).join(', ')}" and have{' '}
          <strong>{paywallData?.trialDaysRemaining} days remaining</strong> in your trial.
        </p>
        <p className="text-muted">
          To create additional experiences, please choose a plan and payment method for your existing experiences.
          These experiences will continue with the remaining trial period before billing starts.  You can cancel the experiences at any time before billing starts if you do not want to continue using them.
          If you do not want to add a plan to these experiences, you can deactivate them before adding a plan or payment method here.
        </p>
      </div>
      }

      {/* Affiliate Info */}
      {affiliateData && (
        <div className="mb-4">
          <Alert variant="info">
            <i className="bi bi-person-check-fill me-2"></i>
            <strong>Special Offer from {affiliateData.affiliateName}!</strong>
            {affiliatePlans.length > 0 && (
              <div className="mt-2">
                You have access to exclusive plans designed specifically for you.
              </div>
            )}
          </Alert>
        </div>
      )}

      {/* Billing Cycle Switch */}
      <div className="mb-4 text-center">
        <div className="d-inline-flex bg-light rounded-pill p-1">
          <Button
            variant={billingCycle === 'monthly' ? 'primary' : 'outline-primary'}
            size="sm"
            className="rounded-pill px-4"
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </Button>
          <Button
            variant={billingCycle === 'yearly' ? 'primary' : 'outline-primary'}
            size="sm"
            className="rounded-pill px-4 ms-1"
            onClick={() => setBillingCycle('yearly')}
          >
            Yearly
            <Badge bg="success" className="ms-2">Save!</Badge>
          </Button>
        </div>
      </div>

      {/* Plan Selection */}
      <div className="mb-4">
        <h6 className="mb-3">Choose your plan:</h6>
        
        {allPlans.length === 0 ? (
          <Alert variant="warning">
            No subscription plans are available at this time. Please contact support.
          </Alert>
        ) : (
          <Row>
            {/* Affiliate Plans */}
            {affiliatePlans.map((plan) => (
              <Col md={6} lg={4} key={plan.id}>
                <PlanCard
                  plan={plan}
                  isSelected={selectedPlanId === plan.id}
                  billingCycle={billingCycle}
                  onSelect={handlePlanSelect}
                />
              </Col>
            ))}
            
            {/* Standard Plans */}
            {standardPlans.map((plan) => (
              <Col md={6} lg={4} key={plan.id}>
                <PlanCard
                  plan={plan}
                  isSelected={selectedPlanId === plan.id}
                  billingCycle={billingCycle}
                  onSelect={handlePlanSelect}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Payment Details */}
      {selectedPlanId && (
        <div className="mb-4">
          <h6 className="mb-3">Payment Information:</h6>
          <div className="p-3 border rounded">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      <div className="d-flex justify-content-between">
        <Button 
          variant="outline-secondary" 
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="primary"
          disabled={!stripe || isProcessing || !selectedPlanId}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            selectedPlan ? `Set up ${selectedPlan.planName} subscription` : 'Select a plan'
          )}
        </Button>
      </div>
    </Form>
  )
}

const EnhancedPaywallModal: React.FC<PaywallModalProps> = ({ 
  show, 
  onHide, 
  paywallData, 
  onPaymentComplete 
}) => {
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>Choose Your Subscription Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Elements stripe={stripePromise}>
          <PaymentForm
            paywallData={paywallData}
            onPaymentComplete={onPaymentComplete}
            onCancel={onHide}
          />
        </Elements>
      </Modal.Body>
    </Modal>
  )
}

export default EnhancedPaywallModal
